// src/services/profileService.js

import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  documentId,
  getDocs,
  limit,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import { auth, db } from "../firebase";
import { getAuthResponseData } from "./authResponseStorage";
import { getDeviceDetails } from "../utils/commonFunctions";
import { buildSearchTokens } from "../utils/searchTokens";
import { readStoredJson, USER_PROFILE_STORAGE_KEY } from "../store/appStorage";
import {
  derivePublicHandleCandidate,
  matchesPublicHandle,
  normalizePublicHandle,
} from "../utils/publicProfile";
import { trimAndLimitProfileText } from "../utils/profileText";
import { attributeSignupToCapsule } from "../utils/measurement";
import { armPromoForSignup } from "../utils/promoRedemption";

export const DEFAULT_NOTIFICATION_PREFERENCES = Object.freeze({
  receivedNewMessage: true,
  sentMessageOpened: true,
  messageRevealDateReached: true,
  campaignNotifications: true,
  privateMessageNearby: true,
});

const withDefaultNotificationPreferences = (preferences) => ({
  ...DEFAULT_NOTIFICATION_PREFERENCES,
  ...(preferences && typeof preferences === "object" ? preferences : {}),
});

// Helper function to get user profile from localStorage
export const getUserProfileFromLocalStorage = () =>
  readStoredJson(USER_PROFILE_STORAGE_KEY, null);

const mapUserBasicInfoDoc = (docSnap) => {
  const data = docSnap.data() || {};
  return {
    uid: docSnap.id,
    displayName: data.displayName || null,
    email: data.email || null,
    phoneNumber: data.phoneNumber || null,
    profileImage: data.profileImage || null,
    publicHandle: data.publicHandle || null,
    userName: data.userName || null,
  };
};

export const addUserConnection = async (currentUserId, targetUserId) => {
  if (!currentUserId || !targetUserId || currentUserId === targetUserId) {
    throw new Error("Two distinct user IDs are required.");
  }

  const now = new Date();

  await Promise.all([
    setDoc(
      doc(db, "users", currentUserId, "connections", targetUserId),
      { connectedAt: now },
      { merge: true }
    ),
    setDoc(
      doc(db, "users", targetUserId, "connections", currentUserId),
      { connectedAt: now },
      { merge: true }
    ),
    updateDoc(doc(db, "users", currentUserId), {
      connections: arrayUnion(targetUserId),
    }),
    updateDoc(doc(db, "users", targetUserId), {
      connections: arrayUnion(currentUserId),
    }),
  ]);
};

export const removeUserConnection = async (currentUserId, targetUserId) => {
  if (!currentUserId || !targetUserId || currentUserId === targetUserId) {
    throw new Error("Two distinct user IDs are required.");
  }

  await Promise.all([
    deleteDoc(doc(db, "users", currentUserId, "connections", targetUserId)),
    deleteDoc(doc(db, "users", targetUserId, "connections", currentUserId)),
    updateDoc(doc(db, "users", currentUserId), {
      connections: arrayRemove(targetUserId),
    }),
    updateDoc(doc(db, "users", targetUserId), {
      connections: arrayRemove(currentUserId),
    }),
  ]);
};

function buildConnectionRequestProfile(profile, fallbackUid) {
  const nextDisplayName = trimAndLimitProfileText(profile?.displayName);
  const nextUserName = trimAndLimitProfileText(profile?.userName);
  const nextPublicHandle = normalizePublicHandle(
    trimAndLimitProfileText(profile?.publicHandle || nextUserName)
  );
  return {
    uid: String(profile?.uid || fallbackUid || "").trim(),
    displayName: nextDisplayName,
    userName: nextUserName,
    publicHandle: nextPublicHandle,
    email: String(profile?.email || "").trim(),
    profileImage: String(profile?.profileImage || "").trim(),
  };
}

async function deleteConnectionRequestPair(currentUserId, otherUserId) {
  await Promise.all([
    deleteDoc(doc(db, "users", currentUserId, "connectionRequests", otherUserId)),
    deleteDoc(doc(db, "users", otherUserId, "sentConnectionRequests", currentUserId)),
    deleteDoc(doc(db, "users", currentUserId, "sentConnectionRequests", otherUserId)),
    deleteDoc(doc(db, "users", otherUserId, "connectionRequests", currentUserId)),
  ]);
}

export const sendConnectionRequest = async (currentUser, targetUser) => {
  const currentUserId = String(currentUser?.uid || "").trim();
  const targetUserId = String(targetUser?.uid || "").trim();

  if (!currentUserId || !targetUserId || currentUserId === targetUserId) {
    throw new Error("Two distinct user IDs are required.");
  }

  const requesterProfile =
    buildConnectionRequestProfile(getUserProfileFromLocalStorage(), currentUserId);
  const targetProfile = buildConnectionRequestProfile(targetUser, targetUserId);

  const existingIncomingRequest = await getDoc(
    doc(db, "users", currentUserId, "connectionRequests", targetUserId)
  );

  if (existingIncomingRequest.exists()) {
    await acceptConnectionRequest(currentUserId, targetUserId);
    return { autoAccepted: true };
  }

  await Promise.all([
    setDoc(doc(db, "users", targetUserId, "connectionRequests", currentUserId), {
      requesterUid: requesterProfile.uid,
      requesterDisplayName: requesterProfile.displayName,
      requesterUsername: requesterProfile.userName,
      requesterPublicHandle: requesterProfile.publicHandle,
      requesterEmail: requesterProfile.email,
      requesterProfileImage: requesterProfile.profileImage,
      requestedAt: serverTimestamp(),
      status: "pending",
    }),
    setDoc(doc(db, "users", currentUserId, "sentConnectionRequests", targetUserId), {
      targetUid: targetProfile.uid,
      targetDisplayName: targetProfile.displayName,
      targetUsername: targetProfile.userName,
      targetPublicHandle: targetProfile.publicHandle,
      targetEmail: targetProfile.email,
      targetProfileImage: targetProfile.profileImage,
      requestedAt: serverTimestamp(),
      status: "pending",
    }),
  ]);

  return { autoAccepted: false };
};

export const acceptConnectionRequest = async (currentUserId, requesterUserId) => {
  if (!currentUserId || !requesterUserId || currentUserId === requesterUserId) {
    throw new Error("Two distinct user IDs are required.");
  }

  await addUserConnection(currentUserId, requesterUserId);
  await deleteConnectionRequestPair(currentUserId, requesterUserId);
};

export const declineConnectionRequest = async (currentUserId, requesterUserId) => {
  if (!currentUserId || !requesterUserId || currentUserId === requesterUserId) {
    throw new Error("Two distinct user IDs are required.");
  }

  await Promise.all([
    deleteDoc(doc(db, "users", currentUserId, "connectionRequests", requesterUserId)),
    deleteDoc(doc(db, "users", requesterUserId, "sentConnectionRequests", currentUserId)),
  ]);
};

export const cancelConnectionRequest = async (currentUserId, targetUserId) => {
  if (!currentUserId || !targetUserId || currentUserId === targetUserId) {
    throw new Error("Two distinct user IDs are required.");
  }

  await Promise.all([
    deleteDoc(doc(db, "users", currentUserId, "sentConnectionRequests", targetUserId)),
    deleteDoc(doc(db, "users", targetUserId, "connectionRequests", currentUserId)),
  ]);
};

export const getConnectionRequestStatus = async (currentUserId, targetUserId) => {
  if (!currentUserId || !targetUserId || currentUserId === targetUserId) {
    return { incoming: false, outgoing: false };
  }

  const [incomingSnap, outgoingSnap] = await Promise.all([
    getDoc(doc(db, "users", currentUserId, "connectionRequests", targetUserId)),
    getDoc(doc(db, "users", currentUserId, "sentConnectionRequests", targetUserId)),
  ]);

  return {
    incoming: incomingSnap.exists(),
    outgoing: outgoingSnap.exists(),
  };
};

export const fetchIncomingConnectionRequests = async (userId) => {
  if (!userId) return [];

  const snap = await getDocs(collection(db, "users", userId, "connectionRequests"));

  const rows = await Promise.all(
    snap.docs.map(async (docSnap) => {
      const data = docSnap.data() || {};
      const requesterUid = String(data.requesterUid || docSnap.id || "").trim();
      const fallbackProfile =
        !String(data.requesterDisplayName || "").trim() ||
        !String(data.requesterPublicHandle || "").trim() ||
        !String(data.requesterProfileImage || "").trim()
          ? await fetchUserProfileFromFirestore(requesterUid)
          : null;

      return {
        uid: requesterUid,
        displayName: String(
          data.requesterDisplayName || fallbackProfile?.displayName || ""
        ).trim(),
        userName: String(
          data.requesterUsername || fallbackProfile?.userName || ""
        ).trim(),
        publicHandle: String(
          data.requesterPublicHandle || fallbackProfile?.publicHandle || ""
        ).trim(),
        email: String(
          data.requesterEmail || fallbackProfile?.email || ""
        ).trim(),
        profileImage: String(
          data.requesterProfileImage || fallbackProfile?.profileImage || ""
        ).trim(),
        requestedAt: data.requestedAt || null,
      };
    })
  );

  return rows;
};

export const fetchOutgoingConnectionRequests = async (userId) => {
  if (!userId) return [];

  const snap = await getDocs(collection(db, "users", userId, "sentConnectionRequests"));

  const rows = await Promise.all(
    snap.docs.map(async (docSnap) => {
      const data = docSnap.data() || {};
      const targetUid = String(data.targetUid || docSnap.id || "").trim();
      const fallbackProfile =
        !String(data.targetDisplayName || "").trim() ||
        !String(data.targetPublicHandle || "").trim() ||
        !String(data.targetProfileImage || "").trim()
          ? await fetchUserProfileFromFirestore(targetUid)
          : null;

      return {
        uid: targetUid,
        displayName: String(
          data.targetDisplayName || fallbackProfile?.displayName || ""
        ).trim(),
        userName: String(
          data.targetUsername || fallbackProfile?.userName || ""
        ).trim(),
        publicHandle: String(
          data.targetPublicHandle || fallbackProfile?.publicHandle || ""
        ).trim(),
        email: String(
          data.targetEmail || fallbackProfile?.email || ""
        ).trim(),
        profileImage: String(
          data.targetProfileImage || fallbackProfile?.profileImage || ""
        ).trim(),
        requestedAt: data.requestedAt || null,
      };
    })
  );

  return rows;
};

// Firestore profile functions
export const updateProfileInFirestore = async (uid, updatedProfile) => {
  if (!uid) {
    throw new Error("UID is undefined.");
  }

  const userRef = doc(db, "users", uid);
  const existingSnap = await getDoc(userRef);
  const existingData = existingSnap.exists() ? existingSnap.data() : {};

  // Destructure out server/client-managed fields that profile edits must not overwrite.
  const { createdAt, fcmTokens, fcmSyncStatus, lastActive, ...profileData } = updatedProfile;
  if (Object.prototype.hasOwnProperty.call(profileData, "displayName")) {
    profileData.displayName = trimAndLimitProfileText(profileData.displayName);
  }
  if (Object.prototype.hasOwnProperty.call(profileData, "userName")) {
    profileData.userName = trimAndLimitProfileText(profileData.userName);
  }
  if (Object.prototype.hasOwnProperty.call(profileData, "publicHandle")) {
    profileData.publicHandle = normalizePublicHandle(
      trimAndLimitProfileText(profileData.publicHandle)
    );
  }
  const mergedProfile = { ...existingData, ...profileData };

  const displayName = trimAndLimitProfileText(mergedProfile.displayName || "");
  const publicHandle =
    normalizePublicHandle(mergedProfile.publicHandle || "") ||
    derivePublicHandleCandidate(mergedProfile) ||
    "";
  const userName = trimAndLimitProfileText(mergedProfile.userName || "");
  const email = mergedProfile.email || "";
  const custom = mergedProfile.searchKeywordsCustom || [];
  if (publicHandle) {
    profileData.publicHandle = publicHandle;
  }
  profileData.searchTokens = buildSearchTokens({ displayName, publicHandle, userName, email, custom });
  if (
    typeof profileData.isDiscoverable !== "boolean" &&
    typeof mergedProfile.isDiscoverable !== "boolean"
  ) {
    profileData.isDiscoverable = true;
  }

  // Now only update with fields in `profileData`, so `createdAt` is never written
  await updateDoc(userRef, profileData);
};

// Is this exact token still registered for the user?
// The Cloud Function prunes tokens FCM reports as unregistered, so a token the
// browser happily keeps handing back can be absent server side.
export const isFcmTokenStored = async (uid, token) => {
  if (!uid || !token) return false;

  const userSnap = await getDoc(doc(db, "users", uid));
  if (!userSnap.exists()) return false;

  const tokens = userSnap.data()?.fcmTokens;
  return Array.isArray(tokens) && tokens.some((entry) => entry?.token === token);
};

//Save FCM Token to Firestore
export const addFcmTokenToFirestore = async (uid, token, metadata = {}) => {
  if (!uid || !token) return;

  const deviceInfo = getDeviceDetails();
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const installationId = String(metadata.installationId || "").trim();
  const userRef = doc(db, "users", uid);
  const userSnap = await getDoc(userRef);
  const userData = userSnap.exists() ? userSnap.data() : {};

  const existingTokens = userData.fcmTokens || [];

  // Remove stale entries for this browser install or this exact token.
  const filteredTokens = existingTokens.filter((entry) => {
    if (!entry) return false;
    if (entry.token === token) return false;
    if (installationId && entry.installationId === installationId) return false;
    return true;
  });

  // Add updated token for the current device
  const nextTokenEntry = {
    token,
    device: deviceInfo.device,
    os: deviceInfo.os,
    browser: deviceInfo.browser,
    timeZone,
    lastUpdated: new Date(),
  };
  if (installationId) nextTokenEntry.installationId = installationId;
  filteredTokens.push(nextTokenEntry);

  await updateDoc(userRef, {
    fcmTokens: filteredTokens,
    timeZone,
  });
};

export const updateFcmSyncStatus = async (uid, status = {}) => {
  if (!uid) return;

  const normalizedStatus =
    status && typeof status === "object"
      ? status
      : { state: String(status || "unknown") };

  await updateDoc(doc(db, "users", uid), {
    fcmSyncStatus: {
      ...normalizedStatus,
      updatedAt: new Date(),
    },
  });
};

//Remove FCM Token from Firestore
export const removeFcmTokenFromFirestore = async (uid) => {
  if (!uid) return;

  const deviceInfo = getDeviceDetails(); // e.g., { device: "SM-G955U", ... }
  const installationId = localStorage.getItem("fcmInstallationId");
  if (!deviceInfo?.device && !installationId) return;

  const userRef = doc(db, "users", uid);
  const userSnap = await getDoc(userRef);
  if (!userSnap.exists()) return;

  const userData = userSnap.data();
  const existingTokens = userData.fcmTokens || [];

  // Prefer removing the current browser install. Fall back to the legacy device field.
  const updatedTokens = existingTokens.filter((entry) => {
    if (!entry) return false;
    if (installationId && entry.installationId === installationId) return false;
    if (!installationId && entry.device === deviceInfo.device) return false;
    return true;
  });

  await updateDoc(userRef, {
    fcmTokens: updatedTokens,
    // Leaving the old "saved" status behind made this doc claim a token it no
    // longer had, which hid broken push installs from monitoring.
    fcmSyncStatus: {
      state: "signed_out",
      installationId: installationId || "",
      updatedAt: new Date(),
    },
  });

  localStorage.removeItem("fcmToken");
  localStorage.removeItem("fcmUserId");
  localStorage.removeItem("fcmEmailKey");
  localStorage.removeItem("fcmTimeZone");
  localStorage.removeItem("fcmLastSyncedAt");
};

// Fetch Firestore profile
export const fetchUserProfileFromFirestore = async (uid) => {
  if (!uid) throw new Error("User ID is required to fetch profile");

  // console.log("Fetching Firestore profile for UID:", uid); // Debugging Log

  const userRef = doc(db, "users", uid);
  const userSnap = await getDoc(userRef);

  if (userSnap.exists()) {
    // console.log("Firestore User Profile Data:", userSnap.data()); // Debugging Log
    return userSnap.data();
  } else {
    console.log("No such user profile in Firestore, returning null.");
    return null; // Return null instead of throwing an error
  }
};

export const fetchUserProfileByHandle = async (handle) => {
  const normalizedHandle = normalizePublicHandle(handle);
  if (!normalizedHandle) return null;

  const usersRef = collection(db, "users");
  const publicHandleSnap = await getDocs(
    query(usersRef, where("publicHandle", "==", normalizedHandle), limit(1))
  );

  if (!publicHandleSnap.empty) {
    const userDoc = publicHandleSnap.docs[0];
    return { uid: userDoc.id, ...userDoc.data() };
  }

  const snap = await getDocs(
    query(usersRef, where("searchTokens", "array-contains", normalizedHandle), limit(25))
  );

  if (snap.empty) {
    return null;
  }

  const matches = snap.docs
    .map((userDoc) => ({ uid: userDoc.id, ...userDoc.data() }))
    .filter((profile) => matchesPublicHandle(profile.userName, normalizedHandle));

  if (!matches.length) {
    const legacyUserNameSnap = await getDocs(
      query(
        usersRef,
        where("userName", ">=", normalizedHandle),
        where("userName", "<=", `${normalizedHandle}\uf8ff`),
        limit(25)
      )
    );

    if (!legacyUserNameSnap.empty) {
      const legacyMatches = legacyUserNameSnap.docs
        .map((userDoc) => ({ uid: userDoc.id, ...userDoc.data() }))
        .filter((profile) => matchesPublicHandle(profile.userName, normalizedHandle));

      if (legacyMatches.length) {
        return legacyMatches[0];
      }
    }

    return null;
  }

  const exactNamedMatch = matches.find(
    (profile) => String(profile.userName || "").trim().toLowerCase() === normalizedHandle
  );

  return exactNamedMatch || matches[0];
};
// Fetch all users basic info from firestore
export const fetchAllUsersBasicInfoFromFirestore = async () => {
  try {
    const usersRef = collection(db, 'users');
    const snapshot = await getDocs(usersRef);

    const users = snapshot.docs.map(mapUserBasicInfoDoc);

    return users;
  } catch (error) {
    console.error('Error fetching users:', error);
    return [];
  }
};

export const fetchUsersBasicInfoByIds = async (userIds = []) => {
  const ids = Array.from(
    new Set(
      (Array.isArray(userIds) ? userIds : [])
        .map((id) => String(id || "").trim())
        .filter(Boolean)
    )
  );

  if (!ids.length) return [];

  try {
    const usersRef = collection(db, "users");
    const chunks = [];
    for (let index = 0; index < ids.length; index += 10) {
      chunks.push(ids.slice(index, index + 10));
    }

    const snapshots = await Promise.all(
      chunks.map((chunk) => getDocs(query(usersRef, where(documentId(), "in", chunk))))
    );

    const byId = new Map();
    snapshots.forEach((snapshot) => {
      snapshot.docs.forEach((docSnap) => {
        byId.set(docSnap.id, mapUserBasicInfoDoc(docSnap));
      });
    });

    return ids.map((id) => byId.get(id)).filter(Boolean);
  } catch (error) {
    console.error("Error fetching requested users:", error);
    return [];
  }
};
// Delete Firestore profile
export const deleteUserProfileFromFirestore = async (uid) => {
  if (!uid) {
    throw new Error("UID is undefined.");
  }

  const userRef = doc(db, "users", uid);
  await deleteDoc(userRef);
};

export const createUserProfileInFirestore = async (uid, profileData) => {
  if (!uid) {
    throw new Error("UID is undefined.");
  }

  // ensure the 'uid' field is part of the profileData
  if (!profileData.uid) {
    profileData.uid = uid;
  }

  const userRef = doc(db, "users", uid);

  // merge in a server-side timestamp for createdAt
  const prepared = { ...profileData };
  prepared.displayName = trimAndLimitProfileText(prepared.displayName);
  prepared.userName = trimAndLimitProfileText(prepared.userName);
  prepared.publicHandle = normalizePublicHandle(
    trimAndLimitProfileText(prepared.publicHandle)
  );
  prepared.searchTokens = buildSearchTokens({
    displayName: prepared.displayName,
    publicHandle: prepared.publicHandle || derivePublicHandleCandidate(prepared),
    userName: prepared.userName,
    email: prepared.email,
    custom: prepared.searchKeywordsCustom || [],
  });
  if (!prepared.publicHandle) {
    const derivedHandle = derivePublicHandleCandidate(prepared);
    if (derivedHandle) prepared.publicHandle = derivedHandle;
  }
  if (typeof prepared.isDiscoverable !== "boolean") {
    prepared.isDiscoverable = true;
  }
  prepared.notificationPreferences = withDefaultNotificationPreferences(
    prepared.notificationPreferences
  );

  await setDoc(userRef, {
    ...prepared,
    createdAt: serverTimestamp(),
  });
};

// Azure profile fetching function
export const fetchAzureUserProfile = async (email) => {
  const getUserProfileUrl =
    process.env.REACT_APP_GET_KBEAN_USER_PROFILE_BY_EMAIL_URL;
  const apiKey = process.env.REACT_APP_SEARCH_API_KEY;

  console.log("Fetching Azure user profile for email:", email); // Log email

  try {
    const response = await fetch(getUserProfileUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ApiKey: apiKey,
      },
      body: JSON.stringify({ EmailAddress: email }),
    });

    console.log("Azure API response status:", response.status); // Log status code

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    const data = await response.json();

    console.log("Azure profile response data:", data); // Log the entire response

    return data;
  } catch (error) {
    console.error("Error fetching Azure profile:", error); // Log any errors
    throw error; // Re-throw the error to handle it in the calling function
  }
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const fetchAzureUserProfileWithRetry = async (
  email,
  { attempts = 3, delayMs = 700 } = {}
) => {
  let lastError;
  console.info("[profile] Azure profile fetch with retry start:", {
    email,
    attempts,
    delayMs,
  });

  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      console.info(`[profile] Azure profile fetch attempt ${attempt}/${attempts} for:`, email);
      const data = await fetchAzureUserProfile(email);
      if (data && typeof data === "object" && Object.keys(data).length > 0) {
        console.info("[profile] Azure profile fetch success on attempt:", attempt);
        return data;
      }
      lastError = new Error("Azure profile response was empty.");
      console.warn("[profile] Azure profile response empty on attempt:", attempt);
    } catch (error) {
      lastError = error;
      console.warn("[profile] Azure profile fetch error on attempt:", attempt, error);
    }

    if (attempt < attempts) {
      await sleep(delayMs);
    }
  }

  console.error("[profile] Azure profile fetch failed after retries:", lastError);
  throw lastError || new Error("Azure profile fetch failed.");
};
//Get Azure profile By Token
export const fetchAzureUserProfileByToken = async () => {
  const getUserProfileUrl = process.env.REACT_APP_GET_KBEAN_USER_PROFILE_BY_TOKEN_URL;
  const authResponseData = getAuthResponseData();
  const token = authResponseData?.token;

  try {
    const response = await fetch(`${getUserProfileUrl}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      return
    }

    const data = await response.json();

    return data;
  } catch (error) {
    console.error("Error fetching Azure profile:", error);
  }
};
export const updateAzureUserProfile = async (formData) => {
  const updateUserProfileUrl = process.env.REACT_APP_UPDATE_KBEAN_USER_PROFILE_URL;
  const authResponseData = getAuthResponseData();
  const token = authResponseData?.token;

  try {
    const response = await fetch(updateUserProfileUrl, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Failed to update profile. Status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error updating Azure profile:", error);
  }
};

// Check username availability via 3rd party API
export const checkUserNameAvailability = async (userName) => {
  const checkUserNameAvailabilityURL =
    process.env.REACT_APP_CHECK_USERNAME_AVAILABILITY_URL;
  const response = await fetch(`${checkUserNameAvailabilityURL}${userName}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`API request failed with status ${response.status}`);
  }

  const data = await response.json();
  return data.response.message === "UserName available";
};

// Update Azure user profile with new username
export const updateAzureUserProfileUsername = async (userName, email) => {
  const updateUserNameUrl = process.env.REACT_APP_UPDATE_KBEAN_USERNAME;
  const apiKey = process.env.REACT_APP_SEARCH_API_KEY;

  const response = await fetch(updateUserNameUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ApiKey: apiKey,
    },
    body: JSON.stringify({ UserName: userName, EmailAddress: email }),
  });

  if (!response.ok) {
    throw new Error(`API request failed with status ${response.status}`);
  }

  return await response.json();
};
// Update Azure user profile with new username
export const updateAzureUserProfilePicture = async (profilePicture, email) => {
  const url = process.env.REACT_APP_UPDATE_KBEAN_PROFILE_PICTURE;
  const apiKey = process.env.REACT_APP_SEARCH_API_KEY;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ApiKey: apiKey,
    },
    body: JSON.stringify({ ProfilePicture: profilePicture, EmailAddress: email }),
  });

  if (!response.ok) {
    throw new Error(`API request failed with status ${response.status}`);
  }

  return await response.json();
};
// Update Azure user profile with new username
export const updateAzureUserPhoneNumber = async (phoneNumber, email) => {

  const url = process.env.REACT_APP_UPDATE_KBEAN_PHONE_NUMBER;
  const apiKey = process.env.REACT_APP_SEARCH_API_KEY;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ApiKey: apiKey,
    },
    body: JSON.stringify({ PhoneNumber: phoneNumber, EmailAddress: email }),
  });
  if (!response.ok) {
    throw new Error(`API request failed with status ${response.status}`);
  }

  return await response.json();
};
// Fetch challenges created by a user
export const fetchUserChallenges = async (uid, currentUser) => {
  if (!uid) throw new Error("User ID is required to fetch challenges");

  const challengesRef = collection(db, "challenges");

  // If fetching challenges for the current user, include drafts
  let q;
  if (uid === currentUser) {
    // Fetch both published and unpublished challenges for the current user
    q = query(challengesRef, where("creatorInfo.creatorUid", "==", uid));
  } else {
    // Fetch only published challenges for other users
    q = query(
      challengesRef,
      where("creatorInfo.creatorUid", "==", uid),
      where("published", "==", true)
    );
  }

  try {
    const querySnapshot = await getDocs(q);
    const challenges = [];
    querySnapshot.forEach((doc) => {
      challenges.push({ id: doc.id, ...doc.data() });
    });

    return challenges; // Return the fetched challenges
  } catch (error) {
    console.error("Error fetching challenges:", error);
    throw error;
  }
};

// Fetch submissions created by a user
export const fetchUserSubmissions = async (uid, currentUserUid) => {
  if (!uid) throw new Error("User ID is required to fetch submissions");

  const submissionsRef = collection(db, "submissions");
  const q = query(submissionsRef, where("profileData.uid", "==", uid));

  try {
    const querySnapshot = await getDocs(q);
    const submissions = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      // Omit submissions with status "draft" if currentUserUid is not equal to uid
      if (data.status === "draft" && currentUserUid !== uid) {
        return; // Skip this submission
      }
      submissions.push({ id: doc.id, ...data });
    });

    return submissions;
  } catch (error) {
    console.error("Error fetching submissions:", error);
    throw error;
  }
};

// Function to update profile image in the relevant collections
export const updateProfileImageInAllCollections = async (
  userId,
  newProfileImageUrl
) => {
  try {
    // Step 1: Update profile image in "challenges" collection
    const challengesRef = collection(db, "challenges");
    const challengesQuery = query(
      challengesRef,
      where("creatorInfo.creatorUid", "==", userId)
    );
    const challengesSnapshot = await getDocs(challengesQuery);

    await Promise.all(
      challengesSnapshot.docs.map(async (challengeDoc) => {
        const challengeRef = doc(db, "challenges", challengeDoc.id);
        await updateDoc(challengeRef, {
          "creatorInfo.profileImage": newProfileImageUrl,
        });
      })
    );

    console.log("Updated profile image in challenges collection.");

    // Step 2: Update profile image in "submissions" collection
    const submissionsRef = collection(db, "submissions");
    const submissionsQuery = query(
      submissionsRef,
      where("user.uid", "==", userId)
    );
    const submissionsSnapshot = await getDocs(submissionsQuery);

    await Promise.all(
      submissionsSnapshot.docs.map(async (submissionDoc) => {
        const submissionRef = doc(db, "submissions", submissionDoc.id);
        await updateDoc(submissionRef, {
          "user.profileImage": newProfileImageUrl,
        });
      })
    );

    console.log("Updated profile image in submissions collection.");

    // Step 3: Update sender profile image on all time capsules created by this user
    const timeCapsulesRef = collection(db, "timeCapsules");
    const timeCapsulesQuery = query(timeCapsulesRef, where("senderId", "==", userId));
    const timeCapsulesSnapshot = await getDocs(timeCapsulesQuery);

    await Promise.all(
      timeCapsulesSnapshot.docs.map(async (capsuleDoc) => {
        const capsuleRef = doc(db, "timeCapsules", capsuleDoc.id);
        await updateDoc(capsuleRef, {
          senderProfileImage: newProfileImageUrl || "",
        });
      })
    );

    console.log("Updated profile image in timeCapsules collection.");
  } catch (error) {
    console.error("Error updating profile image in all collections:", error);
  }
};

// Function to follow a user
export const followUser = async (currentUser, targetUser) => {
  const followerUserProfile = getUserProfileFromLocalStorage();

  console.log(followerUserProfile.userName);

  try {
    // Construct unique IDs for the followers and following documents
    const followerDocId = `${targetUser.uid}_${currentUser.uid}`; // For followers collection
    const followingDocId = `${currentUser.uid}_${targetUser.uid}`; // For following collection

    // References to follower and following collections
    const followerRef = doc(db, "followers", followerDocId);
    const followingRef = doc(db, "following", followingDocId);

    // Add the follower to the target user's followers collection
    await setDoc(followerRef, {
      uid: targetUser.uid,
      followerUid: followerUserProfile.uid,
      followerDisplayName: followerUserProfile.displayName,
      followerUsername: followerUserProfile.userName,
      followerProfileImage: followerUserProfile.profileImage,
      timestamp: new Date(),
    });

    // Add the current user to the target user's following collection
    await setDoc(followingRef, {
      uid: currentUser.uid,
      followingUid: targetUser.uid,
      followingDisplayName: targetUser.displayName,
      followingUsername: targetUser.userName,
      followingProfileImage: targetUser.profileImage,
      timestamp: new Date(),
    });

    console.log("Successfully followed the user");
  } catch (error) {
    console.error("Error following user:", error);
    throw error;
  }
};

// Function to unfollow a user
export const unfollowUser = async (currentUser, targetUser) => {
  try {
    // Construct unique IDs for the followers and following documents
    const followerDocId = `${targetUser.uid}_${currentUser.uid}`; // For followers collection
    const followingDocId = `${currentUser.uid}_${targetUser.uid}`; // For following collection

    // References to follower and following collections
    const followerRef = doc(db, "followers", followerDocId);
    const followingRef = doc(db, "following", followingDocId);

    // Delete follower and following documents
    await deleteDoc(followerRef);
    await deleteDoc(followingRef);

    console.log("Successfully unfollowed the user");
  } catch (error) {
    console.error("Error unfollowing user:", error);
    throw error;
  }
};

// Function to check if the current user is following the target user
export const isFollowingUser = async (currentUser, targetUserId) => {
  try {
    const followingDocId = `${currentUser.uid}_${targetUserId}`;
    const followingRef = doc(db, "following", followingDocId);
    const followingDoc = await getDoc(followingRef);

    return followingDoc.exists();
  } catch (error) {
    console.error("Error checking following status:", error);
    return false;
  }
};

// Function to fetch followers count for a specific user
export const fetchFollowersCount = async (userId) => {
  try {
    const followersRef = collection(db, "followers");
    const followersQuery = query(followersRef, where("uid", "==", userId));
    const followersSnapshot = await getDocs(followersQuery);
    return followersSnapshot.size;
  } catch (error) {
    console.error("Error fetching followers count:", error);
    throw error;
  }
};

// Function to fetch following count for a specific user
export const fetchFollowingCount = async (userId) => {
  try {
    const followingRef = collection(db, "following");
    const followingQuery = query(followingRef, where("uid", "==", userId));
    const followingSnapshot = await getDocs(followingQuery);
    return followingSnapshot.size;
  } catch (error) {
    console.error("Error fetching following count:", error);
    throw error;
  }
};

export const fetchFollowersForUser = async (userId) => {
  if (!userId) return [];

  const followersRef = collection(db, "followers");
  const followersQuery = query(followersRef, where("uid", "==", userId));
  const followersSnapshot = await getDocs(followersQuery);

  const followerIds = Array.from(
    new Set(
      followersSnapshot.docs
        .map((docSnap) => String(docSnap.data()?.followerUid || "").trim())
        .filter(Boolean)
    )
  );

  const followers = await Promise.all(
    followerIds.map(async (followerUid) => {
      const profile = await fetchUserProfile(followerUid);
      return profile ? { ...profile, uid: followerUid } : null;
    })
  );

  return followers.filter(Boolean);
};

export const fetchFollowingForUser = async (userId) => {
  if (!userId) return [];

  const followingRef = collection(db, "following");
  const followingQuery = query(followingRef, where("uid", "==", userId));
  const followingSnapshot = await getDocs(followingQuery);

  const followingIds = Array.from(
    new Set(
      followingSnapshot.docs
        .map((docSnap) => String(docSnap.data()?.followingUid || "").trim())
        .filter(Boolean)
    )
  );

  const following = await Promise.all(
    followingIds.map(async (followingUid) => {
      const profile = await fetchUserProfile(followingUid);
      return profile ? { ...profile, uid: followingUid } : null;
    })
  );

  return following.filter(Boolean);
};

export const getLovedTimeCapsuleIds = (profile) => {
  if (!profile || typeof profile !== "object") {
    return [];
  }

  return Array.isArray(profile.lovedCapsuleIds)
    ? profile.lovedCapsuleIds
        .map((id) => String(id || "").trim())
        .filter(Boolean)
    : [];
};

export const setLovedTimeCapsule = async (userId, capsuleId, shouldLove) => {
  if (!userId || !capsuleId) {
    throw new Error("User ID and capsule ID are required.");
  }

  const userRef = doc(db, "users", userId);
  const fieldUpdate = shouldLove ? arrayUnion(capsuleId) : arrayRemove(capsuleId);

  await setDoc(
    userRef,
    {
      lovedCapsuleIds: fieldUpdate,
    },
    { merge: true }
  );
};

// Function to fetch artist profile with artworks from Kbean Azure backend for social video interactions
export const getArtistProfile = async (username) => {
  const apiKey = process.env.REACT_APP_SEARCH_API_KEY;
  const url = process.env.REACT_APP_GET_KBEAN_ARTIST_PROFILE_BY_USERNAME;

  const body = JSON.stringify({
    Sort: '{"Sale.SaleStart":-1}',
    Page: 0,
    Size: 2000,
    GroupBy: "",
    FilterMatureArtworks: true,
    FilterKbeanSelectOnly: false,
    Criteria: [
      {
        type: "username",
        value: username,
      },
    ],
  });

  const headers = {
    "Content-Type": "application/json",
    ApiKey: apiKey,
  };

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: headers,
      body: body,
    });

    if (!response.ok) {
      throw new Error("Network response was not ok");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Failed to fetch artist profile:", error);
    throw error;
  }
};

// Helper function to fetch a user profile from Firestore by UID
export const fetchUserProfile = async (uid) => {
  if (!uid) throw new Error("User ID is required to fetch profile");

  const userRef = doc(db, "users", uid);
  const userSnap = await getDoc(userRef);

  if (userSnap.exists()) {
    return { id: userSnap.id, ...userSnap.data() };
  } else {
    console.log("No such user profile in Firestore, returning null.");
    return null;
  }
};

// Fetch the current logged-in user's profile from Firestore
export const fetchCurrentUserProfile = async (currentUser) => {
  if (currentUser) {
    try {
      const userProfile = await fetchUserProfile(currentUser.uid);
      return userProfile || currentUser; // Return Firestore profile or auth profile
    } catch (error) {
      console.error("Error fetching Firestore profile:", error);
      throw error;
    }
  } else {
    console.log("No user is signed in.");
    return null; // Return null if no user is logged in
  }
};

// Function to fetch memberships for a user from Firestore
export const fetchUserMembershipsFromFirestore = async (uid) => {
  console.log(`Fetching memberships for UID: ${uid}`); // Debugging Log
  const membershipsRef = collection(db, "memberships");
  const q = query(membershipsRef, where("userId", "==", uid));

  const querySnapshot = await getDocs(q);
  const memberships = [];

  querySnapshot.forEach((doc) => {
    console.log(`Found membership: ${doc.id}`); // Debugging Log
    memberships.push({ id: doc.id, ...doc.data() });
  });

  return memberships;
};

// Fetch the current logged-in user's memberships from Firestore
export const fetchCurrentUserMemberships = async (currentUser) => {
  if (currentUser) {
    try {
      const userMemberships = await fetchUserMembershipsFromFirestore(
        currentUser.uid
      );
      return userMemberships;
    } catch (error) {
      console.error("Error fetching Firestore memberships:", error);
      throw error;
    }
  } else {
    console.log("No user is signed in.");
    return []; // Return an empty array if no user is logged in
  }
};

export const getOrCreateUserProfile = async (user) => {
  // 0) ensure auth.currentUser is up-to-date
  if (auth.currentUser) {
    await auth.currentUser.reload();
    user = auth.currentUser;
  }

  // 1) try existing Firestore profile
  let profile = await fetchUserProfileFromFirestore(user.uid);
  if (profile) {
    const patch = {};

    // 1a) retrofit: ensure hasSignedUpWithINSSA is true
    if (profile.hasSignedUpWithINSSA !== true) {
      patch.hasSignedUpWithINSSA = true;
    }

    // 1b) if user just linked a phone but Firestore is missing it, patch it
    if (user.phoneNumber && !profile.phoneNumber) {
      patch.phoneNumber = user.phoneNumber;
    }

    // 1c) write any needed patches
    if (!Array.isArray(profile.searchTokens) || profile.searchTokens.length === 0) {
      patch.searchTokens = buildSearchTokens({
        displayName: profile.displayName || user.displayName || "",
        publicHandle: profile.publicHandle || derivePublicHandleCandidate(profile),
        userName: profile.userName || "",
        email: profile.email || user.email || "",
        custom: profile.searchKeywordsCustom || [],
      });
    }
    if (typeof profile.isDiscoverable !== "boolean") {
      patch.isDiscoverable = true;
    }
    if (!profile.publicHandle) {
      const derivedHandle = derivePublicHandleCandidate(profile);
      if (derivedHandle) patch.publicHandle = derivedHandle;
    }
    const profilePrefs = profile.notificationPreferences;
    const missingPrefKeys = Object.keys(DEFAULT_NOTIFICATION_PREFERENCES).some(
      (key) => profilePrefs?.[key] === undefined
    );
    if (!profilePrefs || typeof profilePrefs !== "object" || missingPrefKeys) {
      patch.notificationPreferences = withDefaultNotificationPreferences(profilePrefs);
    }

    // 1e) hydrate incomplete docs (for example, docs created with only sign-in markers)
    const hasDisplayName = String(profile.displayName || "").trim().length > 0;
    const hasEmail = String(profile.email || "").trim().length > 0;
    const hasUserName = String(profile.userName || "").trim().length > 0;

    if (!hasDisplayName || !hasEmail || !hasUserName) {
      console.info("[profile] existing profile incomplete, hydrating from Azure/Firebase fallback uid:", user?.uid, {
        hasDisplayName,
        hasEmail,
        hasUserName,
      });
      let azureProfile = null;
      if (user.email) {
        try {
          azureProfile = await fetchAzureUserProfileWithRetry(user.email);
        } catch (error) {
          console.warn("Azure profile fetch failed during hydration; using Firebase fallback.", error);
        }
      }

      if (!hasDisplayName) {
        patch.displayName =
          azureProfile?.fullName || user.displayName || profile.displayName || "New User";
      }
      if (!hasEmail) {
        patch.email = azureProfile?.emailAddress || user.email || profile.email || "";
      }
      if (!hasUserName) {
        patch.userName =
          azureProfile?.userName ||
          user.email ||
          profile.email ||
          profile.userName ||
          "new-user";
      }
      if (!String(profile.bio || "").trim()) {
        patch.bio = azureProfile?.biography || "";
      }
      if (!String(profile.profileImage || "").trim()) {
        patch.profileImage = azureProfile?.profilePicture || user.photoURL || "";
      }
    }

    // 1d) write any needed patches
    if (Object.keys(patch).length > 0) {
      console.info("[profile] patching existing Firestore profile uid:", user?.uid, "fields:", Object.keys(patch));
      await updateProfileInFirestore(user.uid, patch);
      // reflect in local object so we return the latest view
      profile = { ...profile, ...patch };
    }

    return profile;
  }

  // 2) no Firestore doc → fetch from Azure and build data
  let azureProfile = {};
  if (user.email) {
    try {
      azureProfile = (await fetchAzureUserProfileWithRetry(user.email)) || {};
    } catch (error) {
      console.warn("Azure profile fetch failed for new user; creating Firestore profile from Firebase auth data.", error);
    }
  }
  const data = {
    uid: user.uid,
    displayName: azureProfile.fullName || user.displayName || "New User",
    email: azureProfile.emailAddress || user.email,
    userName: azureProfile.userName || user.email,
    publicHandle: derivePublicHandleCandidate({
      publicHandle: azureProfile.publicHandle,
      userName: azureProfile.userName || user.email,
      email: azureProfile.emailAddress || user.email,
    }),
    bio: azureProfile.biography || "",
    profileImage: azureProfile.profilePicture || user.photoURL || "",
    hasSignedUpWithINSSA: true, // set on create
    isDiscoverable: true,
  };

  if (user.phoneNumber) {
    data.phoneNumber = user.phoneNumber;
  }

  // 3) write it
  await createUserProfileInFirestore(user.uid, data);

  // 3a) Direct-journey signup attribution (§3.5): this is a brand-new account, so credit the
  // capsule that drove it. Fire-and-forget — the server resolves the driving campaign from this
  // device's recent open/impression events; never blocks or fails signup.
  attributeSignupToCapsule();

  // 3b) Arm the promo-redeem modal for this fresh signup. If the user arrived via
  // a ?promo=CODE link, the global modal host will open the offer on the landing
  // screen. One-shot; a no-op when there is no pending code.
  armPromoForSignup();

  // 4) re-fetch so we return exactly what lives in Firestore
  profile = await fetchUserProfileFromFirestore(user.uid);
  return profile;
};

/**
 * Fetches the current user's Firestore document.
 * @returns {Promise<import("firebase/firestore").DocumentSnapshot>} A promise that resolves with the user document snapshot.
 * @throws {Error} If there is no authenticated user or the document does not exist.
 */
export async function getUserDoc() {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("No authenticated user found.");
  }

  const userDocRef = doc(db, "users", user.uid);
  const userDocSnap = await getDoc(userDocRef);

  if (!userDocSnap.exists()) {
    throw new Error("User document does not exist.");
  }

  return userDocSnap;
}
export const forgotPassword = async (email) => {
  const forgotPasswordApiUrl = process.env.REACT_APP_FORGOT_PASSWORD;
  const response = await fetch(forgotPasswordApiUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ EmailAddress: email })
  });
  console.log("response",response)
  if (response.ok) {
    return "Please check your email for instructions to reset your password.";
  } else {
    throw new Error(`API request failed with status ${response.status}`);
  }
};
